import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Database, AlertTriangle, UserX } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { Loading, fdate, fdatetime } from './cdm/ui';
import CdmResumen from './cdm/CdmResumen';
import CdmMensual from './cdm/CdmMensual';
import CdmMiembros from './cdm/CdmMiembros';
import CdmRed from './cdm/CdmRed';
import CdmFhct from './cdm/CdmFhct';
import CdmComisiones from './cdm/CdmComisiones';
import CdmGastos from './cdm/CdmGastos';
import CdmOperaciones from './cdm/CdmOperaciones';
import CdmRegistrar from './cdm/CdmRegistrar';
import CdmPendientes from './cdm/CdmPendientes';
import CdmAjustes, { CdmImport } from './cdm/CdmAjustes';

/*
  CdmPanel — Dirección (solo superadmin).
  La plataforma es la fuente de verdad del CDM: altas, cobros, aportaciones FHCT, operaciones
  y posiciones de los socios, bricks, comisiones, gastos y cuadros de dirección. El Sheet se
  importó al principio y se puede «Actualizar desde el Sheet» (Ajustes) sin perder lo creado aquí.
*/
const SUBTABS = [
  ['resumen', 'Resumen', CdmResumen],
  ['mensual', 'Mensual', CdmMensual],
  ['miembros', 'Miembros', CdmMiembros],
  ['red', 'Red de brokers', CdmRed],
  ['fhct', 'FHCT y bricks', CdmFhct],
  ['operaciones', 'Operaciones', CdmOperaciones],
  ['comisiones', 'Comisiones', CdmComisiones],
  ['gastos', 'Gastos', CdmGastos],
  ['registrar', 'Registrar operación', CdmRegistrar],
  ['pendientes', 'Pendientes (en pruebas)', CdmPendientes],
  ['ajustes', 'Ajustes', CdmAjustes],
];

export default function CdmPanel({ initialSub, staging }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [sub, setSub] = useState(() => (SUBTABS.some(([k]) => k === initialSub) ? initialSub : 'resumen'));
  const [rev, setRev] = useState(0);
  const [memberFilter, setMemberFilter] = useState(null);
  const load = useCallback(() => {
    dashboardApi.cdmStatus().then((s) => { setStatus(s); setError(null); }).catch((e) => setError(e.message));
  }, []);
  useEffect(load, [load, sub]);
  const onImported = () => { load(); setRev((r) => r + 1); setSub('resumen'); };

  if (!status) return <Loading error={error} />;

  if (!status.imported) {
    return (
      <div className="space-y-5 max-w-3xl" data-testid="cdm-panel">
        <div className="rounded-2xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-5 flex gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#c9a84c]/15 flex items-center justify-center shrink-0"><Database size={18} className="text-[#c9a84c]" /></div>
          <div>
            <p className="text-white font-medium">Cuadro de mando (CDM) en la plataforma</p>
            <p className="text-sm text-gray-400 mt-1">Importa una sola vez el CDM de Google Sheets. A partir de ahí todo se registra aquí: cada alta pagada con Stripe entra sola y las comisiones, bricks y cuadros se recalculan al momento.</p>
          </div>
        </div>
        <CdmImport imported={false} onDone={onImported} cdm={status} />
      </div>
    );
  }

  const Comp = (SUBTABS.find(([k]) => k === sub) || SUBTABS[0])[2];
  const meta = status.meta || {};
  return (
    <div className="space-y-5" data-testid="cdm-panel">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
        <span>Importado el {fdate(meta.imported_at)}{meta.file_name ? ` · ${meta.file_name}` : ''}</span>
        {meta.anonymized && <span className="text-amber-200">Datos anonimizados (entorno de pruebas)</span>}
        {(status.staging ?? staging) && !meta.anonymized && <span className="text-amber-200">Entorno de pruebas</span>}
        {status.last_change_at && <span>Último cambio: {fdatetime(status.last_change_at)}</span>}
        <span className="ml-auto">Eventos y bootcamp: <Link to="/dashboard/admin?tab=eventos&sub=eventos" className="text-[#c9a84c] hover:underline">Eventos</Link> · <Link to="/dashboard/admin?tab=eventos&sub=bootcamps" className="text-[#c9a84c] hover:underline">Bootcamps</Link></span>
      </div>
      {status.missing_broker > 0 && (
        <div className="rounded-xl border border-red-400/30 bg-red-400/[0.06] p-4 flex flex-wrap items-center gap-3" role="alert" data-testid="cdm-alert-sin-broker">
          <UserX size={18} className="text-red-300 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm text-white font-medium">{status.missing_broker === 1 ? '1 socio sin broker asignado' : `${status.missing_broker} socios sin broker asignado`}</p>
            <p className="text-xs text-gray-400">Sus comisiones no se generan hasta que se asigne el broker. {status.missing_broker_ids.slice(0, 12).join(', ')}{status.missing_broker > 12 ? '…' : ''}</p>
          </div>
          <button onClick={() => { setMemberFilter('sin_broker'); setSub('miembros'); setRev((r) => r + 1); }} className="rounded-lg bg-red-400/15 border border-red-400/30 px-3 py-1.5 text-sm text-red-200 hover:bg-red-400/25">Revisar</button>
        </div>
      )}
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-white/[0.06] bg-white/[0.02] p-1" role="tablist">
        {SUBTABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={sub === k} onClick={() => { setMemberFilter(null); setSub(k); }} data-testid={`cdm-sub-${k}`}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${sub === k ? 'bg-[#c9a84c]/15 text-[#c9a84c]' : 'text-gray-400 hover:text-white'}`}>
            {label}
            {k === 'pendientes' && status.pending > 0 && <span className="ml-1.5 inline-flex items-center gap-1 text-amber-300"><AlertTriangle size={11} />{status.pending}</span>}
          </button>
        ))}
      </div>
      <div key={`${sub}-${rev}`} className="min-w-0">
        <Comp imported onImported={onImported} cdm={{ ...status, staging: status.staging ?? staging }} initialFilter={sub === 'miembros' ? memberFilter : undefined} onChanged={load} />
      </div>
    </div>
  );
}
